<script lang="ts" setup>
import { ref, watch, inject, onBeforeUnmount, nextTick } from 'vue';
import HotspotImage from '@/assets/images/hotspot.svg?url';
import CollapseFieldset from '@/components/CollapseFieldset.vue';
import { generateUniqueKey } from '@/utils';

import type { Ref } from 'vue';
import type { Observer } from '@babylonjs/core/Misc/observable';
import type { Scene } from '@babylonjs/core/scene';
import type { Viewer, Extension, Nullable } from '@immersive-tech/viewer-engine';
import type { IManifest } from '@/types/IManifest';
import type { HotspotPicker, ManifestHandler, Hotspots } from '@/viewerExtensions';

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const manifest = inject('manifest') as Ref<IManifest>;
const activeHotspot = inject('activeHotspot') as Ref<Nullable<string>>;
const enableDragDropFileUpload = inject('enableDragDropFileUpload') as Ref<boolean>;
const hotspotImage = ref<HTMLImageElement>();
const pickerTargetUpdater = ref<Nullable<Observer<Scene>>>(null);
const isDragging = ref(false);
const hotspotPickerExtension = viewer.value?.extensions.get('HotspotPicker') as Extension<HotspotPicker>;
const hotspotsExtension = viewer.value?.extensions.get('Hotspots') as Extension<Hotspots>;
const manifestHandlerExtension = viewer.value?.extensions.get('ManifestHandler') as Extension<ManifestHandler>;

// updatePicker render observable callback
const updatePickerCallback = () => {
  if (hotspotPickerExtension.crosshairEnabled === true) {
    hotspotPickerExtension.updatePicker();
  }
};

const hotspotDraggablePointerDownHandler = () => {
  if (!viewer.value?.scene) return;

  enableDragDropFileUpload.value = false;

  document.body.style.cursor = 'grabbing';

  viewer.value.renderSemaphore += 1;
  pickerTargetUpdater.value = viewer.value.scene.onBeforeRenderObservable.add(updatePickerCallback);
  isDragging.value = true;

  hotspotPickerExtension.crosshairEnabled = true;
};

const createNewHotspot = (hotspotName: string) => {
  const { currentPoint } = hotspotPickerExtension;
  const hotspotIndex = hotspotPickerExtension.createHotspotPlacer(currentPoint[1]);

  if (hotspotIndex >= 0) {
    const newHotspot = {
      position: [{ x: currentPoint[1].x, y: currentPoint[1].y, z: currentPoint[1].z }],
      startVisible: true,
      order: Object.keys(manifest.value.hotspots).length,
      animations: [],
      perspective: null,
      flatContentURL: null,
      hotspotUpdates: [],
      hotspotName,
    };

    manifest.value.hotspots[hotspotName] = newHotspot;

    if (manifestHandlerExtension.sceneJson) {
      manifestHandlerExtension.sceneJson.hotspots[hotspotName] = newHotspot;
    }

    hotspotsExtension.createHotspot(newHotspot);

    if (hotspotsExtension.isCurrentLevelTopLevel) {
      hotspotsExtension.currentLevelHotspots.push(newHotspot.hotspotName);
    }

    hotspotsExtension.topLevelHotspots.push(newHotspot.hotspotName);
    hotspotsExtension.showHotspot(hotspotName);
    hotspotPickerExtension.attachGizmoToHotspot(hotspotIndex);
  }
};

const documentAndHotspotDraggablePointerUpHandler = (event: PointerEvent) => {
  if (!viewer.value?.scene || isDragging.value === false) return;

  viewer.value.renderSemaphore -= 1;

  enableDragDropFileUpload.value = true;

  document.body.style.cursor = 'default';

  viewer.value.scene.onBeforeRenderObservable.removeCallback(updatePickerCallback);
  isDragging.value = false;

  if (event.target !== viewer.value.canvasElement) return;

  const hotspotName = generateUniqueKey(Object.keys(manifest.value.hotspots), 'New Hotspot');

  createNewHotspot(hotspotName);

  hotspotPickerExtension.crosshairEnabled = false;
  activeHotspot.value = null;

  nextTick(() => {
    activeHotspot.value = hotspotName;
  });
};

const hotspotDraggablePointerEnterHandler = () => {
  document.body.style.cursor = 'grab';
};

const hotspotDraggablePointerLeaveHandler = () => {
  if (isDragging.value === true) return;

  document.body.style.cursor = 'default';
};

const hotspotImageWatcher = (newValue: HTMLImageElement | undefined) => {
  if (!newValue) return;

  newValue.addEventListener('pointerdown', hotspotDraggablePointerDownHandler);
  newValue.addEventListener('pointerup', documentAndHotspotDraggablePointerUpHandler);
  newValue.addEventListener('pointerenter', hotspotDraggablePointerEnterHandler);
  newValue.addEventListener('pointerleave', hotspotDraggablePointerLeaveHandler);
  document.body.addEventListener('pointerup', documentAndHotspotDraggablePointerUpHandler);
};

const unmountHandler = () => {
  document.body.removeEventListener('pointerup', documentAndHotspotDraggablePointerUpHandler);

  hotspotPickerExtension.crosshairEnabled = false;
};

watch(hotspotImage, hotspotImageWatcher);
onBeforeUnmount(unmountHandler);
</script>

<template>
  <CollapseFieldset :enable-collapse="false">
    <template #legend>
      Create Hotspot
    </template>

    <p class="text-xs">
      Add a new hotspot by dragging the target image below to where you want the hotspot to be created in the 3D view
    </p>

    <img ref="hotspotImage" :src="HotspotImage" class="w-[50px] h-[50px] block self-center mx-auto" draggable="false">
  </CollapseFieldset>
</template>
